import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SystemUser } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Shield, UserPlus, Users as UsersIcon, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

export default function Usuarios() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form State
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'user'
  });
  const [isCreating, setIsCreating] = useState(false);

  // Fetch existing users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['system_users'],
    queryFn: () => SystemUser.list()
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.displayName) {
      toast({
        title: "Faltan campos",
        description: "Por favor, rellena todos los campos obligatorios.",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);

    try {
      // 1. Get current Admin's token
      const idToken = await user.getIdToken();
      
      // 2. Call Cloud Function via HTTP
      const response = await fetch(`${import.meta.env.VITE_FUNCTIONS_URL}/crearUsuario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Error desconocido al crear usuario');
      }

      // 3. Success handling
      toast({
        title: "¡Usuario creado!",
        description: `El usuario ${formData.displayName} ha sido creado con éxito.`,
      });

      setFormData({
        email: '',
        password: '',
        displayName: '',
        role: 'user'
      });
      
      // Refresh the users list
      queryClient.invalidateQueries({ queryKey: ['system_users'] });

    } catch (error) {
      console.error("Error creating user:", error);
      toast({
        title: "Error al crear usuario",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 space-y-8 animate-in fade-in zoom-in duration-500">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-400 dark:to-purple-500">
            Gestión de Usuarios
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Administra los accesos y miembros del equipo del CRM
          </p>
        </div>
        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CREATE USER FORM */}
        <Card className="border-indigo-100 dark:border-indigo-900/30 shadow-md">
          <CardHeader className="bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-800">
            <CardTitle className="flex items-center text-lg text-indigo-900 dark:text-indigo-400">
              <UserPlus className="w-5 h-5 mr-2" />
              Crear Nuevo Usuario
            </CardTitle>
            <CardDescription>
              El usuario será notificado o podrá entrar automáticamente con estas credenciales.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleCreateUser} className="space-y-4">
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nombre completo</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    type="text"
                    name="displayName"
                    placeholder="Ej. Laura Gómez"
                    value={formData.displayName}
                    onChange={handleChange}
                    disabled={isCreating}
                    className="pl-9 bg-white dark:bg-gray-900"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Correo Electrónico</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    type="email"
                    name="email"
                    placeholder="laura@roig.net"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isCreating}
                    className="pl-9 bg-white dark:bg-gray-900"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contraseña inicial</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isCreating}
                    className="pl-9 bg-white dark:bg-gray-900"
                    minLength={6}
                    required
                  />
                </div>
                <p className="text-[10px] text-gray-400 pl-1">Mínimo 6 caracteres.</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rol</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={isCreating}
                  className="w-full h-10 px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="user">Usuario normal (Agente)</option>
                  <option value="admin">Administrador global</option>
                </select>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-200 dark:shadow-indigo-900/20"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando en Firebase...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Crear y autorizar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* LIST EXISTING USERS */}
        <Card className="border-gray-100 dark:border-gray-800">
          <CardHeader className="bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-800">
            <CardTitle className="flex items-center text-lg">
              <UsersIcon className="w-5 h-5 mr-2 text-gray-500" />
              Directorio de Equipo
            </CardTitle>
            <CardDescription>Usuarios actuales con acceso al CRM</CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[450px] overflow-y-auto">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold text-sm">
                        {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{u.displayName || 'Sin nombre'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{u.email}</p>
                      </div>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase ${
                        u.role === 'admin' 
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' 
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      }`}>
                        {u.role || 'user'}
                      </span>
                    </div>
                  </div>
                ))}
                
                {users.length === 0 && !isLoading && (
                  <div className="text-center py-10 text-gray-500">
                    No hay usuarios encontrados.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
